/* ============================================================================
   GRUMPI PRODUCT PAGE SURVEY  —  anonymous, no email
   ----------------------------------------------------------------------------
   Fires "Completed Product Page Survey" into Klaviyo against the visitor's
   ANONYMOUS profile. No email is collected and none is required.

   WHAT THIS IS
   A market-intelligence survey, not a barrier survey. Each visitor gets ONE
   randomly chosen question from a pool, then an optional comment box.

   WHY IT ISN'T A LIST OF OBJECTIONS
   The earlier version asked "what's stopping you" with options like "I'm not
   sure about the quality" and "it's more than I want to spend". Reading a menu
   of doubts on a product page can plant doubts that weren't there. At Grumpi's
   volume (0.53% conversion, ~48k sessions/90 days) any conversion damage under
   ~20% relative is statistically undetectable — it would take years to measure.
   An unmeasurable risk on the page where money is made is not worth taking, so
   every question here is neutral or flattering to answer.

   WHY ONE QUESTION AT RANDOM
   The questions are independent, not conditional, so serving one each keeps it
   to a single tap and still fills every question at this traffic level.

   Loaded by snippets/grumpi-product-survey.liquid on product pages.

   PREVIEW MODE is ON. Real customers see nothing. Add ?grumpi_survey=1 to any
   product URL to see it. Set PREVIEW_ONLY:false below to go live for everyone.
   ========================================================================== */
(function () {
  "use strict";

  var CFG = {
    ENABLED: true,
    METRIC: "Completed Product Page Survey",
    COOLDOWN_DAYS: 30,
    STORAGE_KEY: "grumpi_product_survey_v1",

    ASK_COMMENT: true,           // optional free-text screen after the question

    // --- trigger: the reviews block has filled this much of the viewport,
    //     for this long. Reading reviews is an engaged, considered moment. ---
    DWELL_MS: 3500,
    VISIBLE_RATIO: 0.25,
    DEPTH_FALLBACK: 0.62,        // used only if no reviews block is found
    REVIEW_SELECTORS: [
      "#looxReviews", ".loox-reviews", "[data-loox-aggregate]",
      "[id*='loox']", "[class*='loox']",
      "#shopify-product-reviews", "[id*='review']", "[class*='reviews']"
    ],

    RANDOMISE: true,

    PREVIEW_ONLY: true,
    PREVIEW_PARAM: "grumpi_survey",
    DEBUG: false
  };

  if (!CFG.ENABLED) return;

  var script = document.currentScript || (function () {
    var s = document.getElementsByTagName("script");
    return s[s.length - 1];
  })();

  var PRODUCT_HANDLE = (script && script.getAttribute("data-product-handle")) || "";
  var ON_SALE = (function () {
    var raw = String((script && script.getAttribute("data-on-sale")) || "").toLowerCase().trim();
    return raw === "true" ? "true" : (raw === "false" ? "false" : "unknown");
  })();
  var IPAD = /Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
  var DEVICE = (/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || IPAD) ? "mobile" : "desktop";

  function log() { if (CFG.DEBUG && window.console) console.log.apply(console, ["[grumpi-survey]"].concat([].slice.call(arguments))); }

  /* ---------------------------------------------------------------- gating */
  function seen() {
    try {
      var raw = localStorage.getItem(CFG.STORAGE_KEY);
      if (!raw) return false;
      return (Date.now() - JSON.parse(raw).t) < CFG.COOLDOWN_DAYS * 864e5;
    } catch (e) { return false; }
  }
  function markSeen() {
    try { localStorage.setItem(CFG.STORAGE_KEY, JSON.stringify({ t: Date.now() })); } catch (e) {}
  }
  var PREVIEW_KEY = "grumpi_product_survey_preview";
  function previewMode() {
    var m = location.search.match(new RegExp("[?&]" + CFG.PREVIEW_PARAM + "=([^&]*)"));
    try {
      if (m) {
        if (m[1] === "0" || m[1] === "off") { sessionStorage.removeItem(PREVIEW_KEY); return false; }
        sessionStorage.setItem(PREVIEW_KEY, "1");
        return true;
      }
      return sessionStorage.getItem(PREVIEW_KEY) === "1";
    } catch (e) { return !!m; }
  }
  var PREVIEW = previewMode();
  function fromEmail() {
    return /[?&](utm_medium=email|utm_source=klaviyo|_kx=)/i.test(location.search);
  }

  /* ---------------------------------------------------------- the question
     pool. One is served at random. Each carries the decision it informs and
     the investor question it answers, so nobody has to guess later why it's
     here. `weight` biases the draw; `ordinal` means never shuffle the options.
  ------------------------------------------------------------------------- */
  var POOL = [
    {
      id: "trigger", key: "ps_shopping_trigger", weight: 2,
      // DECISION: ad targeting, seasonality, email timing.
      // INVESTOR: demand is event-driven and addressable, not impulse.
      h: "What's the occasion?",
      s: "One tap. Nosy, I know.",
      o: ["Just moved in", "About to move", "Doing up the place",
          "New baby or a kids room", "Replacing something knackered",
          "First place of my own", "No reason, just looking"],
      anchorLast: ["No reason, just looking"]
    },
    {
      id: "discovery", key: "ps_heard_about", weight: 2,
      // DECISION: true channel mix vs what the ad platforms claim.
      // INVESTOR: CAC durability — how much growth is word of mouth, not rented.
      h: "How'd you find me?",
      s: "One tap. I like to know who's talking.",
      o: ["Instagram", "TikTok", "Facebook", "Google", "A friend or family",
          "Saw one at someone's place", "Press or an article", "Can't remember"],
      anchorLast: ["Can't remember"]
    },
    {
      id: "attach", key: "ps_attach_intent", weight: 2,
      // DECISION: bundle strategy, the "build your nook" offer.
      // INVESTOR: the LTV question for a low-frequency category.
      h: "One of me, or a few?",
      s: "Be honest. I don't get jealous.",
      // ORDINAL — rising commitment. Shuffling makes it unreadable.
      o: ["Just the one", "Probably a matching piece too", "I'd do a whole room",
          "Haven't thought that far"],
      ordinal: true
    },
    {
      id: "room", key: "ps_room", weight: 1,
      // DECISION: which landing pages and search terms to build.
      // INVESTOR: use-case expansion beyond the living room.
      h: "Where would I end up?",
      s: "One tap and I'll leave you be.",
      o: ["Living room", "Bedroom", "Nursery or a kids room", "Home office",
          "Rumpus or media room", "Somewhere outside", "Not sure yet"],
      anchorLast: ["Not sure yet"]
    },
    {
      id: "next", key: "ps_make_next", weight: 1,
      // DECISION: what to fund next in the range.
      // INVESTOR: a pre-validated roadmap rather than a guess.
      h: "What should they make next?",
      s: "Cory and Dion are taking requests.",
      o: ["An outdoor range", "A sofa bed", "A bed frame", "A mattress",
          "Matching footrests", "A kids range", "Rugs and cushions"]
    },
    {
      id: "help", key: "ps_would_help", weight: 1,
      // DECISION: what to add to the product page.
      // INVESTOR: conversion upside is identified, not hypothetical.
      // Every option is something Grumpi could ADD. None of them is a doubt —
      // nobody reads "more colours" and starts worrying about the colours.
      h: "Anything I could do better?",
      s: "Go on. I can take it.",
      o: ["More photos or video", "More reviews from real buyers",
          "Seeing it in person somehow", "More colours", "Clearer delivery info",
          "Size and dimensions", "Nothing, I'm sold"],
      anchorLast: ["Nothing, I'm sold"]
    }
  ];

  var TEXT = { key: "ps_anything", h: "Anything else? I'll listen. Reluctantly.", s: "Optional. One line is plenty." };

  function pickQuestion() {
    var bag = [];
    POOL.forEach(function (q) {
      for (var i = 0; i < (q.weight || 1); i++) bag.push(q);
    });
    return bag[Math.floor(Math.random() * bag.length)];
  }

  /* --------------------------------------------------------- randomisation */
  // Fisher-Yates. Removes primacy bias — the tendency to pick whatever sits at
  // the top. It does NOT even out results and shouldn't: if one answer genuinely
  // dominates it should still dominate after shuffling. What it buys is
  // confidence the winner won on merit, not on position.
  function shuffle(arr) {
    var x = arr.slice();
    for (var i = x.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = x[i]; x[i] = x[j]; x[j] = t;
    }
    return x;
  }
  function order(q) {
    if (!CFG.RANDOMISE || q.ordinal) return q.o;
    var anchors = q.anchorLast || [], pinned = [], free = [];
    q.o.forEach(function (item) {
      (anchors.indexOf(item) > -1 ? pinned : free).push(item);
    });
    return shuffle(free).concat(pinned);
  }

  /* ----------------------------------------------------------------- state */
  var answers = {}, opened = false, sent = false, host, root, TRIGGER = "";
  var Q = null, Q_ORDER = null;
  var RESPONSE_ID = "ps_" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

  /* ------------------------------------------------------------- rendering */
  var CSS = [
    ':host{all:initial}',
    '*{box-sizing:border-box;margin:0;padding:0}',
    '.ov{position:fixed;inset:0;background:rgba(38,21,21,.62);z-index:2147483000;',
    '   display:flex;align-items:flex-end;justify-content:center;padding:0;',
    '   font-family:"Red Hat Display",system-ui,sans-serif;font-weight:800;line-height:1.2;',
    '   animation:fade .18s ease}',
    '@media(min-width:640px){.ov{align-items:center;padding:20px}}',
    '@keyframes fade{from{opacity:0}to{opacity:1}}',
    '@keyframes up{from{transform:translateY(18px);opacity:0}to{transform:none;opacity:1}}',
    '.md{width:100%;max-width:470px;background:#FFFDFB;color:#261515;',
    '   border:4px solid #261515;border-bottom-width:8px;border-radius:22px 22px 0 0;',
    '   padding:24px 22px 22px;animation:up .22s ease}',
    '@media(min-width:640px){.md{border-radius:22px}}',
    '.top{display:flex;align-items:center;justify-content:flex-end;gap:12px;margin-bottom:14px}',
    '.x{font-family:"Red Hat Mono",monospace;font-weight:700;font-size:15px;background:none;border:none;',
    '   cursor:pointer;color:#736654;padding:4px 6px;line-height:1}',
    'h2{font-family:"Gasoek One",sans-serif;font-weight:400;text-transform:uppercase;',
    '   font-size:clamp(25px,5.8vw,32px);line-height:.85;margin-bottom:10px}',
    '.sb{font-size:15px;line-height:1.35;color:#382222;margin-bottom:18px}',
    '.opts{display:flex;flex-direction:column;gap:8px}',
    '.opt{width:100%;text-align:left;cursor:pointer;font-family:inherit;font-weight:800;font-size:15px;',
    '   background:#fff;color:#261515;border:3px solid #261515;border-bottom-width:6px;',
    '   border-radius:14px;padding:12px 15px;transition:background .12s}',
    '.opt:active,.opt.sel{background:#FFF398}',
    'textarea{width:100%;min-height:92px;resize:vertical;font-family:inherit;font-weight:800;font-size:15px;',
    '   color:#261515;background:#fff;border:3px solid #261515;border-bottom-width:6px;',
    '   border-radius:14px;padding:12px 14px;margin-bottom:14px}',
    'textarea:focus{outline:3px solid #FFDB89;outline-offset:2px}',
    '.btn{display:block;width:100%;cursor:pointer;font-family:"Red Hat Mono",monospace;font-weight:700;',
    '   text-transform:uppercase;font-size:14px;background:#FFF398;color:#261515;',
    '   border:5px solid #261515;border-bottom-width:10px;border-radius:999px;padding:18px 24px}',
    '.btn:active{transform:translateY(3px)}',
    '.skip{display:block;width:100%;background:none;border:none;cursor:pointer;font-family:"Red Hat Mono",monospace;',
    '   font-weight:700;font-size:11.5px;text-transform:uppercase;color:#736654;margin-top:12px;text-decoration:underline}',
    '.done{text-align:center;padding:16px 0 8px}'
  ].join("");

  var FONT_HREF = "https://fonts.googleapis.com/css2?family=Gasoek+One&family=Red+Hat+Mono:wght@700&family=Red+Hat+Display:wght@800&display=swap";
  function ensureFonts() {
    // @font-face inside a shadow root is ignored by Chromium and WebKit, so the
    // faces must be on the document. The theme self-hosts all three, so this
    // normally does nothing.
    try {
      if (document.fonts && document.fonts.check('700 16px "Red Hat Mono"') &&
          document.fonts.check('400 16px "Gasoek One"')) return;
    } catch (e) {}
    if (document.querySelector('link[data-grumpi-fonts]')) return;
    var l = document.createElement("link");
    l.rel = "stylesheet"; l.href = FONT_HREF; l.setAttribute("data-grumpi-fonts", "");
    document.head.appendChild(l);
  }

  function build() {
    ensureFonts();
    host = document.createElement("div");
    host.setAttribute("data-grumpi-survey", "");
    // The Grumpi theme has `div:empty { display:none }`. A shadow host has no
    // LIGHT-dom children, so CSS counts it as :empty and hides the whole survey.
    // A :host rule inside the shadow root cannot fix this — outer-page styles
    // beat :host by design. Inline !important is the only thing that always wins.
    host.style.setProperty("display", "block", "important");
    host.style.setProperty("position", "static", "important");
    host.style.setProperty("visibility", "visible", "important");
    host.style.setProperty("opacity", "1", "important");
    root = host.attachShadow ? host.attachShadow({ mode: "open" }) : host;
    var st = document.createElement("style"); st.textContent = CSS; root.appendChild(st);
    var ov = document.createElement("div"); ov.className = "ov";
    ov.innerHTML = '<div class="md" role="dialog" aria-modal="true" aria-label="Quick question"></div>';
    root.appendChild(ov);
    document.body.appendChild(host);
    ov.addEventListener("click", function (e) { if (e.target === ov) close(true); });
    document.addEventListener("keydown", onEsc);
  }
  function onEsc(e) { if (e.key === "Escape") close(true); }
  function md() { return root.querySelector(".md"); }
  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"]/g, function (c) {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); }
  function bar() { return '<div class="top"><button class="x" aria-label="Close">&#10005;</button></div>'; }
  function wire() { var x = root.querySelector(".x"); if (x) x.onclick = function () { close(true); }; }

  function showQuestion() {
    Q_ORDER = order(Q);
    var h = bar() + '<h2>' + esc(Q.h) + '</h2><p class="sb">' + esc(Q.s) + '</p><div class="opts">';
    Q_ORDER.forEach(function (label, i) { h += '<button class="opt" data-i="' + i + '">' + esc(label) + '</button>'; });
    md().innerHTML = h + '</div>'; wire();
    Array.prototype.forEach.call(root.querySelectorAll(".opt"), function (b) {
      b.onclick = function () {
        b.classList.add("sel");
        var i = +b.dataset.i;
        answers[Q.key] = Q_ORDER[i];
        answers.ps_answer_pos = i + 1;
        setTimeout(CFG.ASK_COMMENT ? showText : finish, 200);
      };
    });
  }

  function showText() {
    md().innerHTML = bar() + '<h2>' + esc(TEXT.h) + '</h2><p class="sb">' + esc(TEXT.s) + '</p>' +
      '<textarea maxlength="600" placeholder="Type away&hellip;"></textarea>' +
      '<button class="btn">THAT\'LL DO</button><button class="skip">Nah, I\'m good</button>';
    wire();
    root.querySelector(".btn").onclick = function () {
      var v = (root.querySelector("textarea").value || "").trim();
      if (v) answers[TEXT.key] = v;
      finish();
    };
    root.querySelector(".skip").onclick = finish;
  }

  function finish() {
    send();
    md().innerHTML = '<div class="done"><h2>Ta. Carry on.</h2>' +
      '<p class="sb" style="margin:12px 0 0">No email needed. I\'m not that needy.</p></div>';
    setTimeout(function () { close(false); }, 1700);
  }

  /* -------------------------------------------------------------- tracking */
  function send() {
    if (sent) return;
    sent = true;
    var props = {
      ps_question: Q ? Q.id : "",          // which question was served
      product_handle: PRODUCT_HANDLE,
      device: DEVICE,
      on_sale: ON_SALE,
      response_id: RESPONSE_ID,
      page_path: location.pathname,
      ps_trigger: TRIGGER,                 // what made the modal appear
      ps_answer_pos: answers.ps_answer_pos || "",
      randomised: CFG.RANDOMISE ? "true" : "false"
    };
    if (Q && answers[Q.key]) props[Q.key] = answers[Q.key];
    if (answers[TEXT.key]) props[TEXT.key] = answers[TEXT.key];
    if (PREVIEW) props.preview = "true";

    log("tracking", props);
    try {
      window.klaviyo = window.klaviyo || [];
      window.klaviyo.push(["track", CFG.METRIC, props]);
    } catch (e) { log("klaviyo push failed", e); }
    try { window.dispatchEvent(new CustomEvent("grumpi:product-survey", { detail: props })); } catch (e) {}
  }

  function close(partial) {
    // a partial answer is still worth having
    if (partial && Q && answers[Q.key]) {
      try {
        var ta = root && root.querySelector("textarea");
        if (ta && ta.value.trim()) answers[TEXT.key] = ta.value.trim();
      } catch (e) {}
      send();
    }
    markSeen();
    document.removeEventListener("keydown", onEsc);
    if (host && host.parentNode) host.parentNode.removeChild(host);
  }

  function open(why) {
    if (opened || (!PREVIEW && seen())) return;
    opened = true; TRIGGER = why || "";
    Q = pickQuestion();
    markSeen();
    build();
    showQuestion();
  }

  /* -------------------------------------------------------------- triggers */
  function findReviews() {
    for (var i = 0; i < CFG.REVIEW_SELECTORS.length; i++) {
      var els = document.querySelectorAll(CFG.REVIEW_SELECTORS[i]);
      for (var j = 0; j < els.length; j++) {
        // must be a real block, not a star rating badge up in the header
        if (els[j].offsetHeight > 300) return els[j];
      }
    }
    return null;
  }

  // How much of the VIEWPORT the element fills. Deliberately NOT
  // IntersectionObserver's intersectionRatio, which is a fraction of the
  // ELEMENT — the Loox block is ~5300px tall in an ~830px viewport, so its ratio
  // can never exceed 0.16 and any threshold above that would never fire.
  function viewportShare(el) {
    var r = el.getBoundingClientRect();
    var vis = Math.min(r.bottom, innerHeight) - Math.max(r.top, 0);
    if (vis <= 0) return 0;
    return vis / Math.min(r.height, innerHeight);
  }

  function watchReviews(el) {
    var dwell = 0;
    var tick = setInterval(function () {
      if (opened) return clearInterval(tick);
      if (viewportShare(el) >= CFG.VISIBLE_RATIO) {
        dwell += 500;
        if (dwell >= (PREVIEW ? 1200 : CFG.DWELL_MS)) { clearInterval(tick); open("reviews"); }
      }
    }, 500);
  }

  function watchDepth() {
    var held = 0;
    var tick = setInterval(function () {
      if (opened) return clearInterval(tick);
      var max = document.documentElement.scrollHeight - innerHeight;
      var depth = max > 0 ? (window.scrollY / max) : 0;
      if (depth >= CFG.DEPTH_FALLBACK) {
        held += 500;
        if (held >= (PREVIEW ? 1200 : CFG.DWELL_MS)) { clearInterval(tick); open("depth"); }
      } else { held = 0; }
    }, 500);
  }

  function arm() {
    var el = findReviews();
    if (el) { log("watching reviews block", el); watchReviews(el); }
    else { log("no reviews block found, using scroll depth"); watchDepth(); }
  }

  function start() {
    if (CFG.PREVIEW_ONLY && !PREVIEW) {
      log("preview-only — add ?" + CFG.PREVIEW_PARAM + "=1 to the URL to see it");
      return;
    }
    if (!PREVIEW) {
      if (seen()) { log("cooled down, skipping"); return; }
      if (fromEmail()) { log("arrived from email, skipping"); return; }
    }
    // Reviews are injected by an app after load, so wait for them to appear.
    var tries = 0;
    var poll = setInterval(function () {
      if (findReviews() || ++tries > 20) { clearInterval(poll); arm(); }
    }, 500);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
